import React from 'react'
import { Box, Flex } from 'theme-ui'
import {
  dateFormat,
  dateWithoutTimeFormat,
  IssueHistoryItem,
  ReportIssueIgnoreDetails,
  ValidationResult,
} from '@spectral/types'
import { Tag, Tooltip } from 'antd'
import { format } from 'date-fns'
import isEmpty from 'lodash/isEmpty'
import IgnoreDetailsView from './ignore-details-view'

import { DateFormat } from '../../../components/formatters'

const IssueValidationDisplayMapper = {
  valid: 'Valid',
  invalid: 'Invalid',
}

interface Props {
  isExpanded: boolean
  detectorDescription?: string
  fingerprint?: string
  ignoreDetails?: ReportIssueIgnoreDetails
  history?: Array<IssueHistoryItem>
}
const IssueCollapseContent = ({
  isExpanded,
  detectorDescription = null,
  fingerprint = null,
  ignoreDetails = null,
  validation = null,
  history = null,
}) => {
  if (
    !detectorDescription &&
    !fingerprint &&
    !validation &&
    !ignoreDetails &&
    !history
  )
    return null

  const renderValidationResult = () => {
    if (!validation) return null

    if (validation.result === ValidationResult.Error) {
      return (
        <Box>
          The validation of this secret has been errored during the last scan at{' '}
          <DateFormat date={validation?.lastValidation} />
          <br />
          Please scan again for updated result
        </Box>
      )
    }
    return (
      <Box>
        This issue has been identified as{' '}
        <strong>
          <code>{IssueValidationDisplayMapper[validation.result]}</code>{' '}
        </strong>
        at <DateFormat date={validation?.lastValidation} />
      </Box>
    )
  }

  return (
    <Flex
      sx={{
        width: '100%',
        flexDirection: 'column',
        my: '15px',
        gap: '10px',
        transition: 'max-height 0.3s ease-in-out',
        maxHeight: isExpanded ? 'auto' : 0,
        overflow: 'hidden',
      }}
    >
      {detectorDescription && (
        <Box>
          <Box sx={{ fontWeight: 'bold' }}>Detector description:</Box>
          <Box>{detectorDescription}</Box>
        </Box>
      )}
      {validation && (
        <Box>
          <Box sx={{ fontWeight: 'bold' }}>Validation result:</Box>
          {renderValidationResult()}
        </Box>
      )}
      {ignoreDetails && <IgnoreDetailsView ignoreDetails={ignoreDetails} />}
      {!isEmpty(history) && (
        <Box>
          <Box sx={{ fontWeight: 'bold' }}>Status change history:</Box>
          <IssueHistory history={history} />
        </Box>
      )}
      {fingerprint && (
        <Flex mt={2}>
          <Tag>
            <strong>Fingerprint</strong> {fingerprint}
          </Tag>
        </Flex>
      )}
    </Flex>
  )
}

const IssueHistory = ({ history }) => {
  return (
    <Box>
      {history.map((item) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            padding: 2,
            marginBottom: 1,
            borderRadius: '4px',
            border: '1px solid rgba(0, 0, 0, 0.06)',
          }}
        >
          <Box
            sx={{
              fontSize: 1,
              minWidth: 'fit-content',
            }}
          >
            <strong>On:</strong>{' '}
            <Tooltip
              title={format(new Date(item.timestamp), dateFormat)}
              placement="top"
            >
              {format(new Date(item.timestamp), dateWithoutTimeFormat)}
            </Tooltip>
          </Box>

          <Box
            sx={{
              fontSize: 1,
              minWidth: 'fit-content',
            }}
          >
            <strong>Status:</strong> {item.issueStatus}
            {item.metadata?.ignoreType && ` (${item.metadata.ignoreType})`}
          </Box>

          <Box
            sx={{
              fontSize: 1,
              minWidth: 'fit-content',
            }}
          >
            <strong>By:</strong>{' '}
            <Tooltip
              title={item.actor.email}
              placement="top"
            >{`${item.actor.firstName} ${item.actor.lastName}`}</Tooltip>
          </Box>

          {item.metadata?.comment && (
            <Tooltip title={item.metadata.comment} placement="top">
              <Box
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 1,
                }}
              >
                <strong>Comment:</strong> {item.metadata.comment}
              </Box>
            </Tooltip>
          )}
        </Box>
      ))}
    </Box>
  )
}

export default IssueCollapseContent
